import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Wind, 
  Droplets, Thermometer, Eye, Sunrise, Sunset, MapPin,
  Umbrella, Shirt, AlertTriangle, Calendar, Activity,
  ChevronRight, RefreshCw, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Weather condition icons mapping
const WEATHER_ICONS = {
  Clear: Sun,
  Clouds: Cloud,
  Rain: CloudRain,
  Drizzle: CloudRain,
  Thunderstorm: CloudLightning,
  Snow: CloudSnow,
  Mist: Cloud,
  Fog: Cloud,
  Haze: Cloud
};

// Rating colors
const RATING_COLORS = {
  excellent: 'text-emerald-400',
  good: 'text-green-400',
  fair: 'text-yellow-400',
  poor: 'text-orange-400',
  varies: 'text-blue-400'
};

const RATING_BG = {
  excellent: 'bg-emerald-500/20 border-emerald-500/30',
  good: 'bg-green-500/20 border-green-500/30',
  fair: 'bg-yellow-500/20 border-yellow-500/30',
  poor: 'bg-orange-500/20 border-orange-500/30',
  varies: 'bg-blue-500/20 border-blue-500/30'
};

export function WeatherCard({ destination, compact = false, onWeatherLoad }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchWeather = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/weather/city/${encodeURIComponent(destination)}`);
      setWeather(response.data);
      if (onWeatherLoad) {
        onWeatherLoad(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not fetch weather');
    } finally {
      setLoading(false);
    }
  }, [destination, onWeatherLoad]);

  useEffect(() => {
    if (destination) {
      fetchWeather();
    }
  }, [destination, fetchWeather]);

  if (loading) {
    return (
      <div className={`rounded-2xl bg-[var(--bg-card)] border border-[var(--border-primary)] p-6 ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--color-primary)]" />
          <span className="text-[var(--text-secondary)]">Loading weather...</span>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return null;
  }

  const current = weather.current;
  const WeatherIcon = WEATHER_ICONS[current?.condition?.main] || Sun;

  if (compact) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-[var(--bg-card)] to-[var(--bg-secondary)] border border-[var(--border-primary)]"
      >
        <div className="flex items-center gap-2">
          <WeatherIcon className="w-8 h-8 text-[var(--color-gold)]" />
          <div>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {current?.temperature?.current}{current?.temperature?.unit}
            </p>
            <p className="text-xs text-[var(--text-secondary)]">{current?.condition?.description}</p>
          </div>
        </div>
        <div className="flex gap-4 ml-auto text-sm">
          <div className="flex items-center gap-1 text-[var(--text-secondary)]">
            <Droplets className="w-4 h-4" />
            {current?.humidity}%
          </div>
          <div className="flex items-center gap-1 text-[var(--text-secondary)]">
            <Wind className="w-4 h-4" />
            {current?.wind?.speed} {current?.wind?.unit}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${RATING_BG[current?.travel_rating] || RATING_BG.good}`}>
          {current?.travel_rating}
        </span>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-br from-[var(--bg-card)] via-[var(--bg-secondary)] to-[var(--bg-card)] border border-[var(--border-primary)] overflow-hidden"
      data-testid="weather-card"
    >
      {/* Header */}
      <div className="relative p-6 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-[var(--color-primary)]" />
              <h3 className="font-semibold text-[var(--text-primary)]">
                {weather.location?.name}, {weather.location?.country}
              </h3>
            </div>
            <div className="flex items-center gap-4">
              <WeatherIcon className="w-16 h-16 text-[var(--color-gold)]" />
              <div>
                <p className="text-5xl font-bold text-[var(--text-primary)]">
                  {current?.temperature?.current}
                  <span className="text-2xl">{current?.temperature?.unit}</span>
                </p>
                <p className="text-[var(--text-secondary)]">{current?.condition?.description}</p>
              </div>
            </div>
          </div>
          
          <div className={`px-4 py-2 rounded-full border ${RATING_BG[current?.travel_rating] || RATING_BG.good}`}>
            <span className={`text-sm font-semibold capitalize ${RATING_COLORS[current?.travel_rating] || RATING_COLORS.good}`}>
              {current?.travel_rating} for Travel
            </span>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="relative grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/10">
          <div className="text-center">
            <Thermometer className="w-5 h-5 mx-auto mb-1 text-orange-400" />
            <p className="text-xs text-[var(--text-secondary)]">Feels Like</p>
            <p className="font-semibold text-[var(--text-primary)]">{current?.temperature?.feels_like}{current?.temperature?.unit}</p>
          </div>
          <div className="text-center">
            <Droplets className="w-5 h-5 mx-auto mb-1 text-blue-400" />
            <p className="text-xs text-[var(--text-secondary)]">Humidity</p>
            <p className="font-semibold text-[var(--text-primary)]">{current?.humidity}%</p>
          </div>
          <div className="text-center">
            <Wind className="w-5 h-5 mx-auto mb-1 text-cyan-400" />
            <p className="text-xs text-[var(--text-secondary)]">Wind</p>
            <p className="font-semibold text-[var(--text-primary)]">{current?.wind?.speed} {current?.wind?.unit}</p>
          </div>
          <div className="text-center">
            <Eye className="w-5 h-5 mx-auto mb-1 text-purple-400" />
            <p className="text-xs text-[var(--text-secondary)]">Visibility</p>
            <p className="font-semibold text-[var(--text-primary)]">{current?.visibility} km</p>
          </div>
        </div>
      </div>
      
      {/* Sunrise/Sunset */}
      <div className="flex justify-center gap-8 py-4 border-b border-[var(--border-primary)]">
        <div className="flex items-center gap-2">
          <Sunrise className="w-5 h-5 text-orange-400" />
          <span className="text-[var(--text-secondary)]">Sunrise</span>
          <span className="font-medium text-[var(--text-primary)]">{current?.sunrise}</span>
        </div>
        <div className="flex items-center gap-2">
          <Sunset className="w-5 h-5 text-pink-400" />
          <span className="text-[var(--text-secondary)]">Sunset</span>
          <span className="font-medium text-[var(--text-primary)]">{current?.sunset}</span>
        </div>
      </div>
      
      {/* 5-Day Forecast */}
      {weather.forecast?.length > 0 && (
        <div className="p-6">
          <h4 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-4">
            5-Day Forecast
          </h4>
          <div className="grid grid-cols-5 gap-3">
            {weather.forecast.slice(0, 5).map((day, index) => {
              const DayIcon = WEATHER_ICONS[day.condition?.main] || Sun;
              return (
                <motion.div
                  key={day.date}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-primary)]"
                >
                  <p className="text-xs font-medium text-[var(--text-secondary)] mb-2">
                    {index === 0 ? 'Today' : day.day_name?.slice(0, 3)}
                  </p>
                  <DayIcon className="w-8 h-8 mx-auto mb-2 text-[var(--color-gold)]" />
                  <p className="font-semibold text-[var(--text-primary)]">
                    {day.temperature?.max}°
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {day.temperature?.min}°
                  </p>
                  {day.rain_probability > 0 && (
                    <div className="flex items-center justify-center gap-1 mt-2 text-blue-400">
                      <Droplets className="w-3 h-3" />
                      <span className="text-xs">{day.rain_probability}%</span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export function WeatherRecommendations({ weather }) {
  if (!weather?.recommendations) return null;
  
  const { recommendations } = weather;
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-[var(--bg-card)] border border-[var(--border-primary)] overflow-hidden"
      data-testid="weather-recommendations"
    >
      <div className="p-6 border-b border-[var(--border-primary)]">
        <h3 className="font-['Playfair_Display'] text-xl font-bold text-[var(--text-primary)]">
          Travel Recommendations
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{recommendations.summary}</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 p-6">
        {/* Packing List */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Shirt className="w-5 h-5 text-[var(--color-primary)]" />
            <h4 className="font-semibold text-[var(--text-primary)]">What to Pack</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {recommendations.packing_list?.slice(0, 8).map((item, index) => (
              <span 
                key={index}
                className="px-3 py-1.5 rounded-full bg-[var(--bg-secondary)] text-[var(--text-secondary)] text-sm border border-[var(--border-primary)]"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
        
        {/* Activity Suggestions */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-[var(--color-secondary)]" />
            <h4 className="font-semibold text-[var(--text-primary)]">Suggested Activities</h4>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-2">Outdoor</p>
              <div className="flex flex-wrap gap-2">
                {recommendations.activity_suggestions?.outdoor?.map((activity, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-sm border border-emerald-500/20"
                  >
                    {activity}
                  </span>
                ))}
              </div>
            </div>
            <div className="pt-2">
              <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-2">Indoor</p>
              <div className="flex flex-wrap gap-2">
                {recommendations.activity_suggestions?.indoor?.slice(0, 4).map((activity, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-sm border border-blue-500/20"
                  >
                    {activity}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Weather Alerts */}
      {recommendations.weather_alerts?.length > 0 && (
        <div className="p-6 border-t border-[var(--border-primary)] bg-[var(--bg-secondary)]">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h4 className="font-semibold text-[var(--text-primary)]">Weather Alerts</h4>
          </div>
          <div className="space-y-2">
            {recommendations.weather_alerts.map((alert, index) => (
              <div 
                key={index}
                className={`p-3 rounded-xl border ${
                  alert.severity === 'high' 
                    ? 'bg-red-500/10 border-red-500/20 text-red-400' 
                    : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                }`}
              >
                <p className="text-sm">{alert.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Best Times */}
      {recommendations.best_sightseeing_times?.length > 0 && (
        <div className="p-6 border-t border-[var(--border-primary)]">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-[var(--color-gold)]" />
            <h4 className="font-semibold text-[var(--text-primary)]">Best Days for Sightseeing</h4>
          </div>
          <div className="space-y-2">
            {recommendations.best_sightseeing_times.map((time, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-primary)]"
              >
                <span className="font-medium text-[var(--text-primary)]">{time.day}</span>
                <span className="text-sm text-[var(--text-secondary)]">{time.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export function WeatherBadge({ destination, onClick }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchWeather = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/weather/city/${encodeURIComponent(destination)}`);
      setWeather(response.data);
    } catch (err) {
      console.error('Weather fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [destination]);

  useEffect(() => {
    if (destination) {
      fetchWeather();
    }
  }, [destination, fetchWeather]);

  if (loading || !weather) return null;

  const current = weather.current;
  const WeatherIcon = WEATHER_ICONS[current?.condition?.main] || Sun;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/30 transition-colors"
    >
      <WeatherIcon className="w-4 h-4 text-[var(--color-gold)]" />
      <span className="text-white text-sm font-medium">
        {current?.temperature?.current}{current?.temperature?.unit}
      </span>
    </button>
  );
}

export default function WeatherIntelligence({ destination }) {
  const [weather, setWeather] = useState(null);
  
  return (
    <div className="space-y-6">
      <WeatherCard destination={destination} onWeatherLoad={setWeather} />
      <WeatherRecommendations weather={weather} />
    </div>
  );
}
